<script setup lang="ts">
import { computed } from "vue"

import moment from "moment"
import CopyButton from "@Modules/shared/Components/CopyButton.vue"

//
// Props
interface Props {
  codes: string[]
  created: string
}

const props = defineProps<Props>()

//
// Emit
interface Events {
  (e: "close"): void
}
const emit = defineEmits<Events>()

//
// Computed
const backupCodes = computed(() => props.codes.join(","))

const creationDate = computed(() => moment(props.created).format("lll"))

const downloadUrl = computed(() => {
  const codes = props.codes.join("\r\n")
  const date = new Date().toLocaleDateString()
  const text = `TinyURL Two-Factor Authentication Backup Codes\nGenerated: ${date}\n\n${codes}\n\nKeep these codes somewhere safe. Each code can only be used once.`
  const blob = new Blob([text], { type: "text/plain" })
  return URL.createObjectURL(blob)
})

//
// Methods
const close = (): void => {
  emit("close")
}
</script>

<template>
  <focus-trap>
    <div
      tabindex="-1"
      class="modal d-block _backup_codes_modal"
      @keyup.esc="close"
    >
      <div class="modal-dialog modal-dialog-centered modal-557px">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title font-weight-bold">
              Two-Factor Authentication Backup Codes
            </h5>
            <button
              type="button"
              class="close _close"
              @click="close"
            >
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="alert alert-warning">
              <p>
                If you lost access to your authentication app, you may use these
                backup codes as an alternative to log into your account.
              </p>
              <p class="font-weight-semibold mb-0">
                Please note:
              </p>
              <ul>
                <li>Keep these codes somewhere safe but accessible</li>
                <li>You can only use each code once</li>
              </ul>
            </div>

            <div class="text-muted text-right text-sm mb-2 _created">
              Generated on: {{ creationDate }}
            </div>

            <div class="py-3 px-2 border rounded bg-light text-muted">
              <div class="two-factor-backup-codes-modal__codes-grid">
                <span
                  v-for="code in codes"
                  :key="code"
                  class="text-center _code"
                >
                  {{ code }}
                </span>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <copy-button
              :value="backupCodes"
              class="btn btn-outline-secondary _copy"
            >
              Copy
            </copy-button>

            <a
              :href="downloadUrl"
              class="btn btn-outline-primary _download"
              download="tinyurl-2FA-backup-codes.txt"
            >
              Download
            </a>

            <button
              class="btn btn-default _close_footer"
              @click="close"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </focus-trap>
</template>

<style lang="scss" scoped>
.two-factor-backup-codes-modal__codes-grid {
  display: grid;
  grid-template-columns: 1fr;

  @media (min-width: 576px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
}
</style>
